import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { faCircleCheck, faCircle } from "@fortawesome/free-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { PASSWORD_RULES } from "./passwordRules";

const h = React.createElement;

type Props = {
	password: string;
	variant?: "inline" | "list";
	confirmPassword?: string;
	showMatch?: boolean; // list variant only
	className?: string;
};

const PasswordHint: React.FC<Props> = ({
	password = "",
	variant = "list",
	confirmPassword = "",
	showMatch = false,
	className = "",
}) => {
	const { t, i18n } = useTranslation();
	const results = PASSWORD_RULES.map((r) => ({
		...r,
		passed: r.test(password),
	}));

	if (variant === "inline") {
		const isZh = i18n.language?.startsWith("zh");
		const sep = isZh ? "、" : ", ";
		const [first, ...rest] = results;

		const restNodes = rest.map((r, i) => {
			const suffix =
				i < rest.length - 2
					? sep
					: i === rest.length - 2
						? ` ${t("and")} `
						: isZh
							? "。"
							: ".";
			return h(
				React.Fragment,
				{ key: r.key },
				h(
					"span",
					{ className: r.passed ? "rule-passed" : "" },
					t(r.inlineLabel)
				),
				suffix
			);
		});

		return h(
			"div",
			{
				className: `password-hint password-hint--inline ${className}`,
				"aria-live": "polite",
			},
			h(FontAwesomeIcon, { icon: faCircleInfo, className: "me-1" }),
			h(
				"span",
				{ className: first.passed ? "rule-passed" : "" },
				t(first.inlineLabel)
			),
			isZh ? "，" : ", ",
			`${t("including")} `,
			...restNodes
		);
	}

	const items = results.map((r) => ({
		key: r.key as string,
		label: t(r.listLabel),
		passed: r.passed,
	}));
	if (showMatch) {
		items.push({
			key: "match",
			label: t("Passwords match"),
			passed: confirmPassword.length > 0 && password === confirmPassword,
		});
	}

	return h(
		"ul",
		{
			className: `password-hint password-hint--list ${className}`,
			"aria-live": "polite",
		},
		items.map((item) =>
			h(
				"li",
				{ key: item.key, className: item.passed ? "rule-passed" : "" },
				h(FontAwesomeIcon, {
					icon: item.passed ? faCircleCheck : faCircle,
					className: "me-2",
				}),
				item.label
			)
		)
	);
};

export default PasswordHint;
